import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { easeInOut, pivotAt, visibleRect } from '../fit';
import type { Insets } from '../scene';

/**
 * Where the habitat scenes are drawn (M110, the user's third brief: "Selecting Reef, Blue Water or another habitat
 * should activate a dedicated 3D scene composition ... its own camera and camera controls"; "Reuse the existing WebGL
 * renderer, canvas ...; only the active visualization needs to be rendered"). One habitat scene at a time, its own
 * scene graph and its own camera, drawn by the page's renderer on its canvas while the whole tank's waits; one set of
 * controls for them, made once a page and handed each scene's camera and limits as it opens, so switching adds no
 * listener to the canvas. A scene left is freed (`dispose`), its camera's place remembered by its key, so a habitat
 * opened again is seen from where the player left it (the brief: "Restore the previous camera position when returning
 * to an already visited habitat if practical"), its home view a press away (`resetView`).
 */

type Vec3 = readonly [number, number, number];

/** How a habitat's camera looks and is held: its home view, its lens, its controls' limits and the room it keeps. */
export interface HabitatCameraProfile {
  /** The default composition: where the camera stands and the point it looks at. */
  home: { position: Vec3; target: Vec3 };
  /** The vertical field of view of the part of the screen the panels leave, degrees, for its shape (width over height). */
  fov(aspect: number): number;
  near: number;
  far: number;
  /** The controls' nearest and farthest from the point turned about, m (the nearest the close inspection's). */
  distance: readonly [number, number];
  /** The camera's angle from straight up, radians, its least and most. */
  polar: readonly [number, number];
  /**
   * The least angle from straight up it may be lifted to where a ridge or an outcrop leaves it nowhere to stand within
   * `polar` (M241, the overhaul's brief, section 10: "0–25° pitch, rising to about 35° past ridges smoothly"), radians;
   * eased back to `polar`'s least once past, at `HABITAT_HOST.ridgeRate`. None where the scene sets none.
   */
  ridgePolar?: number;
  /** The box the camera and the point it looks at are kept in, world space. */
  bounds: { min: Vec3; max: Vec3 };
  /** The least room kept between the camera and what lies under it, m. */
  clearance: number;
  /**
   * How the player moves it: `orbit`, OrbitControls' own (a finger or the mouse turns it round the point it looks at,
   * two fingers pinch and pan, the right button pans), the habitats' since M110; or `explore` (M125, the user's sixth
   * brief, "One-finger drag: Pan across the reef"), a finger or the mouse's left button moving it over the floor the way
   * it faces, two fingers pinching to zoom, twisting to turn it round and drawn together to turn and tilt it
   * (`HABITAT_HOST.gesture`), the right button turning and tilting it, the point it looks at kept its height over the
   * floor as it goes (`HostedScene.terrainAt`).
   */
  controls?: 'orbit' | 'explore';
  /** The most it turns round, degrees, the home view's direction in its middle: `HABITAT_HOST.turnDeg` unless said. */
  turnDeg?: number;
}

/** A habitat scene as the host draws it: its graph, its camera, its limits, and the height of what is solid under a point. */
export interface HostedScene {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly profile: HabitatCameraProfile;
  /** The top of whatever is solid under a point (its floor, an outcrop), m, -Infinity where nothing is. */
  groundAt(x: number, z: number): number;
  /** The floor's own height at a point, m, smooth under what stands on it: what an `explore` camera follows, if it has one. */
  terrainAt?(x: number, z: number): number;
  dispose(): void;
}

/** A camera's place and the point it looks at. */
export interface HabitatView {
  position: [number, number, number];
  target: [number, number, number];
  /** Whether the player has moved it from the home view (turned, zoomed, panned or following). */
  own: boolean;
}

/**
 * Whether the zoom may aim at an object hit under the pointer: a mesh drawn, it and all it hangs from shown (three.js's
 * raycaster ignores visibility: the Reef's drawn boulders, hidden once the kit's rocks lie in their place, had caught
 * it, the review's), and not a sprite batch, whose one base quad stands where none of its sprites is drawn.
 */
export function aimable(o: THREE.Object3D): boolean {
  if (!(o instanceof THREE.Mesh) || o.geometry instanceof THREE.InstancedBufferGeometry) return false;
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return false;
  return true;
}

/**
 * The host's motion: `moveS` a move to a view (the home view, an animal followed); `followM` how near the camera comes
 * to an animal it is asked to follow, m, or farther where the animal's span would not fit; `followRate` the share a
 * second it catches up with it (each JUDGED); `turnDeg` the most the player turns the camera round the point it looks
 * at, degrees, the home view's direction in its middle, where a scene says no other (the user's word, M124: "for the
 * habitat view, reduce the horizontal viewing angle from 360 degrees to 180 degrees"). An `explore` camera's (M125):
 * `terrainRate` the share a second the point it looks at catches up with the floor's height under it as it moves (the
 * brief's "Smooth changes in camera height"); `gesture` two fingers' gesture, read as a pinch, a twist or a drag by
 * whichever has moved its fingers `lockPx` CSS px first, and only that until a finger lifts (the brief's "distinguishes
 * pinching, twisting and translating two fingers so they do not trigger conflicting movements"); `ridgeRate` the share a
 * second a camera lifted past a ridge eases back to its scene's least tilt (M241, the brief's "smoothly": about three
 * quarters of the way back in a second once the ridge is behind it); each JUDGED.
 */
export const HABITAT_HOST = { moveS: 0.9, followM: 3.5, followRate: 3, turnDeg: 180, terrainRate: 4, ridgeRate: 1.5, gesture: { lockPx: 12 } } as const;

/** A two-finger gesture's reading: the fingers' distance apart and the angle between them, rad, and their middle, CSS px. */
export interface Pair {
  d: number;
  a: number;
  m: [number, number];
}

/** Two touches' reading. */
function pairOf([ax, ay]: [number, number], [bx, by]: [number, number]): Pair {
  return { d: Math.hypot(bx - ax, by - ay), a: Math.atan2(by - ay, bx - ax), m: [(ax + bx) / 2, (ay + by) / 2] };
}

/** An angle's turn from another, wrapped to within half a turn either way, rad. */
function turnOf(a: number, from: number): number {
  const d = a - from;
  return Math.atan2(Math.sin(d), Math.cos(d));
}

/**
 * The gesture two fingers make from where they went down, by how far each kind has moved them, CSS px (a pinch by
 * their distance apart, a twist by the arc each finger has swept, a drag by their middle), or none until one has moved
 * them `HABITAT_HOST.gesture.lockPx`; the largest taken.
 */
function gestureOf(start: Pair, now: Pair): 'pinch' | 'twist' | 'drag' | null {
  const moved = { pinch: Math.abs(now.d - start.d), twist: (Math.abs(turnOf(now.a, start.a)) * (start.d + now.d)) / 4, drag: Math.hypot(now.m[0] - start.m[0], now.m[1] - start.m[1]) };
  const [kind, px] = (Object.entries(moved) as ['pinch' | 'twist' | 'drag', number][]).reduce((a, b) => (b[1] > a[1] ? b : a));
  return px >= HABITAT_HOST.gesture.lockPx ? kind : null;
}

export class HabitatHost {
  readonly controls: OrbitControls;
  private active: HostedScene | null = null;
  private key = '';
  private readonly remembered = new Map<string, HabitatView>();
  private insets: Insets = { top: 0, right: 0, bottom: 0 };
  private moving: { from: THREE.Vector3; fromTarget: THREE.Vector3; to: THREE.Vector3; toTarget: THREE.Vector3; start: number | null; own: boolean } | null = null;
  private followed: THREE.Vector3 | null = null;
  private lastUpdate: number | null = null;
  /** Whether the camera stands where its home view put it, unmoved by the player. */
  private atHome = true;
  /** The camera's offset from the point it looks at where it last stood as it may (`hold`), or none in a scene new to it. */
  private held: THREE.Vector3 | null = null;
  /** Where the camera and the point it looks at last stood as they may (`hold`), or none in a scene new to it (M228). */
  private stood: { camera: THREE.Vector3; target: THREE.Vector3 } | null = null;
  /** A point to work with in `hold`, kept to spare a frame's allocations. */
  private readonly probe = new THREE.Vector3();
  /** Another, for the step `hold` slides along. */
  private readonly step = new THREE.Vector3();
  /** Whether the scene's camera is an `explore` camera (M125). */
  private explore = false;
  /** The floor's height under the point an `explore` camera looks at, eased, or none until it is next read. */
  private eased: number | null = null;
  /** The scene's least polar angle and the least it may be lifted to past a ridge (M241), or null where it sets none. */
  private polarLeast = 0;
  private ridgeLeast: number | null = null;
  /** The canvas's size the active camera was last fitted to, px. */
  private fitted: [number, number] = [0, 0];
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();

  constructor(private readonly canvas: HTMLCanvasElement) {
    // A camera of its own until a scene hands it one.
    this.controls = new OrbitControls(new THREE.PerspectiveCamera(), canvas);
    this.controls.enabled = false;
    this.controls.enableDamping = true;
    this.controls.zoomToCursor = true;
    this.controls.screenSpacePanning = true;
    // An `explore` camera panned over the floor: a finger drawn up the screen moves it the way it faces, level (the
    // floor's plane, OrbitControls' own when its screen-space panning is off). Off for the pan alone, as the zoom to the
    // cursor reads that switch too and, off, aims the camera again at the point it looked at whenever it looks within 20°
    // of level (the review's: a pinch at a phone's edge had turned the Reef's home view 19.95°).
    const internals = this.controls as unknown as OrbitInternals;
    const panUp = internals._panUp.bind(this.controls);
    internals._panUp = (distance, matrix) => {
      if (!this.explore) return panUp(distance, matrix);
      const v = new THREE.Vector3().setFromMatrixColumn(matrix, 0);
      internals._panOffset.add(v.crossVectors(this.controls.object.up, v).multiplyScalar(distance));
    };
    // A turn, a zoom or a pan the player's own: the camera theirs, not the home view's, and any move to a view let go.
    let grabbed: THREE.Vector3[] | null = null;
    // Only its own: the controls switched off (another scene's or the whole tank's turn) take a finger's lost lift, sent
    // them by `pointerHygiene.ts`, with a start and an end that are no player's (the review's).
    this.controls.addEventListener('start', () => {
      if (!this.controls.enabled) return;
      this.moving = null;
      grabbed = [this.controls.object.position.clone(), this.controls.target.clone()];
    });
    // A tap that moves nothing leaves it home.
    this.controls.addEventListener('end', () => {
      if (!this.controls.enabled) {
        grabbed = null;
        return;
      }
      if (grabbed && (grabbed[0]?.distanceTo(this.controls.object.position) ?? 0) + (grabbed[1]?.distanceTo(this.controls.target) ?? 0) > 1e-3) this.atHome = false;
      grabbed = null;
    });
    // A zoom aimed at what is under the pointer, as the whole tank's is (`scene.ts`), so a coral approached is the one
    // the pointer is on: the point turned about moved along the line of sight to its depth.
    // Its zoom then moving the point it looks at toward the pointer itself: the floor followed from where that lands (the
    // second review's: off the middle, the camera had drifted up to 0.05 m on its own after the wheel, 0.27 m after a
    // pinch).
    canvas.addEventListener('wheel', (e) => {
      this.aimAt(e.clientX, e.clientY);
      this.eased = null;
    }, { capture: true, passive: true });
    const touches = new Map<number, [number, number]>();
    // An `explore` camera's two fingers (M125), read as one gesture from where they went down (`gestureOf`); OrbitControls
    // leaves them alone (`touches.TWO` none, `open`).
    let pair: { start: Pair; last: Pair; kind: 'pinch' | 'twist' | 'drag' | null } | null = null;
    const two = (): Pair => pairOf(...([...touches.values()] as [[number, number], [number, number]]));
    canvas.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return;
      touches.set(e.pointerId, [e.clientX, e.clientY]);
      if (pair?.kind) this.controls.dispatchEvent({ type: 'end' });
      pair = null;
      if (touches.size === 2) {
        const now = two();
        this.aimAt(...now.m);
        if (this.explore && this.controls.enabled) pair = { start: now, last: now, kind: null };
      }
    }, { capture: true });
    canvas.addEventListener('pointermove', (e) => {
      if (!touches.has(e.pointerId)) return;
      touches.set(e.pointerId, [e.clientX, e.clientY]);
      if (pair && touches.size === 2) this.gesture(pair, two());
    }, { capture: true });
    // A finger lifted: the gesture's end told before OrbitControls starts the finger left on a pan (in the capture, as
    // its own cancel's listener is older than this one: the review's, a twist ended by a cancel had been told no move);
    // two fingers left after a third lifts, a gesture of their own again.
    const lift = (e: PointerEvent): void => {
      touches.delete(e.pointerId);
      if (pair?.kind) this.controls.dispatchEvent({ type: 'end' });
      pair = null;
      if (touches.size === 2 && this.explore && this.controls.enabled) {
        const now = two();
        pair = { start: now, last: now, kind: null };
      }
    };
    canvas.addEventListener('pointerup', lift, { capture: true });
    canvas.addEventListener('pointercancel', lift, { capture: true });
  }

  /**
   * Two fingers' move on an `explore` camera: once read as a gesture, that alone, from where they went down; a pinch
   * zooms toward their middle, a twist turns the camera round as the fingers turn (the floor turned under them), a drag
   * turns it round and tilts it as one finger turns the whole tank's.
   */
  private gesture(pair: { start: Pair; last: Pair; kind: 'pinch' | 'twist' | 'drag' | null }, now: Pair): void {
    if (!this.controls.enabled || !this.active) return;
    if (!pair.kind) {
      pair.kind = gestureOf(pair.start, now);
      if (!pair.kind) return;
      this.controls.dispatchEvent({ type: 'start' });
    }
    const c = this.controls as unknown as OrbitInternals;
    const height = Math.max(1, this.canvas.clientHeight);
    if (pair.kind === 'pinch') {
      c._updateZoomParameters(...now.m);
      c._dollyOut(now.d / Math.max(1, pair.last.d));
    } else if (pair.kind === 'twist') c._rotateLeft(-turnOf(now.a, pair.last.a));
    else {
      c._rotateLeft((2 * Math.PI * (now.m[0] - pair.last.m[0])) / height);
      c._rotateUp((2 * Math.PI * (now.m[1] - pair.last.m[1])) / height);
    }
    pair.last = now;
    // Carried out at once, as OrbitControls' own handlers do, so a gesture lifted within its frame is a move; a pinch's
    // point looked at moved toward the fingers, the floor followed from there (the wheel's, above).
    this.controls.update();
    if (pair.kind === 'pinch') this.eased = null;
  }

  private aimAt(clientX: number, clientY: number): void {
    const s = this.active;
    if (!s || !this.controls.enabled) return;
    const box = this.canvas.getBoundingClientRect();
    this.pointer.set(((clientX - box.left) / box.width) * 2 - 1, -((clientY - box.top) / box.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, s.camera);
    const hit = this.raycaster.intersectObjects(s.scene.children, true).find((h) => aimable(h.object));
    if (!hit) return;
    const forward = s.camera.getWorldDirection(new THREE.Vector3());
    const pivot = pivotAt(s.camera.position.toArray(), forward.toArray(), hit.point.toArray(), this.controls.minDistance, this.controls.maxDistance);
    if (pivot) {
      this.controls.target.set(...pivot);
      // The floor followed from the new point, not eased toward it from the old (the review's: a zoom had drifted the
      // camera 0.59 m on its own).
      this.eased = null;
    }
  }

  /** The scene drawn now, or none (the whole tank's turn). */
  scene(): HostedScene | null {
    return this.active;
  }

  /**
   * Opens a scene, the one before freed (its view remembered), seen from where the player last left the scene of the
   * same `key` (a habitat at a place) or else from its home view, at once: the brief's fade covers the change.
   */
  open(next: HostedScene, key: string): void {
    this.close();
    this.active = next;
    this.key = key;
    const p = next.profile;
    this.controls.object = next.camera;
    this.controls.minDistance = p.distance[0];
    this.controls.maxDistance = p.distance[1];
    [this.controls.minPolarAngle, this.controls.maxPolarAngle] = p.polar;
    [this.polarLeast, this.ridgeLeast] = [p.polar[0], p.ridgePolar ?? null];
    // Its way of moving (M125): an `explore` camera panned over the floor by a finger or the left button, turned and
    // tilted by the right, its two fingers its own (`gesture`).
    this.explore = p.controls === 'explore';
    this.controls.mouseButtons = this.explore
      ? { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE }
      : { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
    this.controls.touches = this.explore ? { ONE: THREE.TOUCH.PAN, TWO: null } : { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    this.eased = null;
    // The turn round kept to `turnDeg`, half of it either way of the home view's (M124): the scene seen from its front
    // and its sides, never from behind. OrbitControls holds it at every update, a move to a view and a follow's too. An
    // end on the seam at ±180° (the Reef's left, its home view square to the reef) is moved in by a millionth of a
    // radian: a camera standing on it is 1e-16 of its distance across from the point it looks at, which rounds to
    // nothing against the point's own place, its turn read back on the seam's other side and put at the other end, half
    // a circle away (the review's: zoomed in at the Reef's left end, a turn or a follow from it).
    const [home, half] = [Math.atan2(p.home.position[0] - p.home.target[0], p.home.position[2] - p.home.target[2]), THREE.MathUtils.degToRad(p.turnDeg ?? HABITAT_HOST.turnDeg) / 2];
    const seam = (a: number): boolean => Math.PI - Math.abs(Math.atan2(Math.sin(a), Math.cos(a))) < 1e-6;
    this.controls.minAzimuthAngle = half >= Math.PI ? -Infinity : seam(home - half) ? 1e-6 - Math.PI : home - half;
    this.controls.maxAzimuthAngle = half >= Math.PI ? Infinity : seam(home + half) ? Math.PI - 1e-6 : home + half;
    this.controls.enabled = true;
    this.held = null;
    this.stood = null;
    this.resize();
    const view = this.remembered.get(key);
    this.place(view ?? { position: [...p.home.position], target: [...p.home.target], own: false });
  }

  /** Frees the scene drawn, its camera's view remembered; the controls idle until the next. */
  close(): void {
    const s = this.active;
    if (!s) return;
    this.remembered.set(this.key, this.view());
    s.dispose();
    this.active = null;
    this.followed = null;
    this.moving = null;
    this.controls.enabled = false;
  }

  /** The camera put at a view at once. */
  private place(view: { position: readonly number[]; target: readonly number[]; own: boolean }): void {
    const s = this.active;
    if (!s) return;
    this.moving = null;
    this.followed = null;
    s.camera.position.set(view.position[0] ?? 0, view.position[1] ?? 0, view.position[2] ?? 0);
    this.controls.target.set(view.target[0] ?? 0, view.target[1] ?? 0, view.target[2] ?? 0);
    this.eased = null;
    this.stopGlide();
    this.controls.update();
    this.hold();
    this.atHome = !view.own;
  }

  /** Back to the scene's home view (the brief's Reset View), smoothly; the remembered view of its key forgotten. */
  resetView(): void {
    const s = this.active;
    if (!s) return;
    this.remembered.delete(this.key);
    this.followed = null;
    this.moveTo({ position: [...s.profile.home.position], target: [...s.profile.home.target], own: false });
  }

  /**
   * The controls' glide stopped: what a turn, a pan or a zoom had still to carry on (their damping) dropped, so a move
   * to a view ends where it was sent (M125's test: Reset left 2 mm off its home view, a twist's glide carried on over it).
   */
  private stopGlide(): void {
    const c = this.controls as unknown as OrbitInternals;
    c._sphericalDelta.set(0, 0, 0);
    c._panOffset.set(0, 0, 0);
    c._scale = 1;
  }

  /** Moves the camera to a view over `HABITAT_HOST.moveS` (Watch One Day's give-back, a lock's return home). */
  moveTo(view: { position: readonly number[]; target: readonly number[]; own?: boolean }): void {
    const s = this.active;
    if (!s) return;
    this.followed = null;
    this.stopGlide();
    this.moving = {
      from: s.camera.position.clone(), fromTarget: this.controls.target.clone(),
      to: new THREE.Vector3(view.position[0] ?? 0, view.position[1] ?? 0, view.position[2] ?? 0),
      toTarget: new THREE.Vector3(view.target[0] ?? 0, view.target[1] ?? 0, view.target[2] ?? 0),
      start: null, own: view.own ?? true,
    };
  }

  /** Where the camera stands and looks now, and whether that is the player's own. */
  view(): HabitatView {
    const s = this.active;
    return {
      position: (s?.camera.position.toArray() ?? [0, 0, 0]) as [number, number, number],
      target: this.controls.target.toArray() as [number, number, number],
      own: !this.atHome || this.followed !== null,
    };
  }

  /** Whether the camera stands at the home view (the player has not moved it). */
  home(): boolean {
    return this.atHome && this.followed === null;
  }

  /**
   * Follows a point (an animal the player tapped): first moved to, near enough to see it whole, then kept in the
   * view's middle as it swims, the player's own turn and zoom kept; null stops following.
   */
  follow(point: readonly [number, number, number] | null, spanM = 0): void {
    const s = this.active;
    const first = this.followed === null && point !== null;
    this.followed = point ? new THREE.Vector3(...point) : null;
    if (!s || !first || !this.followed) return;
    const across = 2 * Math.tan(THREE.MathUtils.degToRad(s.camera.fov) / 2) * s.camera.aspect;
    const distance = Math.max(HABITAT_HOST.followM, across > 0 ? spanM / across : 0);
    const toward = s.camera.position.clone().sub(this.controls.target).normalize();
    this.moving = { from: s.camera.position.clone(), fromTarget: this.controls.target.clone(), to: this.followed.clone().add(toward.multiplyScalar(distance)), toTarget: this.followed.clone(), start: null, own: true };
    this.atHome = false;
  }

  /**
   * The panels' insets: the camera centered on the part of the canvas they leave, as the whole tank's is (`visibleRect`);
   * fitted again when they move or the canvas's size does (the review's: a window resized under fixed panels had left
   * the scene drawn at its old shape).
   */
  setInsets(next: Insets): void {
    const parent = this.canvas.parentElement ?? document.body;
    const size: [number, number] = [parent.clientWidth, parent.clientHeight];
    if (Math.abs(next.top - this.insets.top) < 1 && Math.abs(next.right - this.insets.right) < 1
      && Math.abs(next.bottom - this.insets.bottom) < 1 && Math.abs((next.left ?? 0) - (this.insets.left ?? 0)) < 1
      && size[0] === this.fitted[0] && size[1] === this.fitted[1]) return;
    this.insets = next;
    this.resize();
  }

  /** The active camera's lens for the canvas's size and the panels: its field of view the scene's for the part left, widened to the frame centered on it. */
  resize(): void {
    const s = this.active;
    if (!s) return;
    const parent = this.canvas.parentElement ?? document.body;
    const [w, h] = [Math.max(parent.clientWidth, 1), Math.max(parent.clientHeight, 1)];
    this.fitted = [parent.clientWidth, parent.clientHeight];
    fitLens(s.camera, s.profile, w, h, this.insets);
  }

  /** The camera moved on to `now` (ms): a move under way, an animal followed, then kept in its bounds and out of the floor. */
  update(now: number): void {
    const s = this.active;
    const dt = this.lastUpdate === null ? 0 : Math.max(0, now - this.lastUpdate) / 1000;
    this.lastUpdate = now;
    if (!s) return;
    if (this.followed && !this.moving) {
      const shift = this.followed.clone().sub(this.controls.target).multiplyScalar(1 - Math.exp(-HABITAT_HOST.followRate * dt));
      this.controls.target.add(shift);
      s.camera.position.add(shift);
    }
    if (this.moving) {
      if (this.followed) this.moving.toTarget.copy(this.followed);
      this.moving.start ??= now;
      const t = Math.min(1, (now - this.moving.start) / (HABITAT_HOST.moveS * 1000));
      const k = easeInOut(t);
      s.camera.position.lerpVectors(this.moving.from, this.moving.to, k);
      this.controls.target.lerpVectors(this.moving.fromTarget, this.moving.toTarget, k);
      if (t >= 1) {
        this.atHome = !this.moving.own && this.followed === null;
        this.moving = null;
      }
    }
    // A camera lifted past a ridge eased back to the scene's least tilt (M241), the controls bringing it down as the
    // least rises, the hold lifting it again where the ridge still stands.
    if (this.controls.minPolarAngle < this.polarLeast) {
      const left = (this.polarLeast - this.controls.minPolarAngle) * Math.exp(-HABITAT_HOST.ridgeRate * dt);
      this.controls.minPolarAngle = left < 1e-4 ? this.polarLeast : this.polarLeast - left;
    }
    this.controls.update();
    // An `explore` camera's point kept its height over the floor as it moves, the floor's under it eased, the camera
    // moved with it (M125, the brief's "Maintain a natural underwater viewing height relative to the seabed", "Avoid
    // abrupt elevation changes"); a move to a view or an animal followed sets its own.
    if (this.explore && !this.moving && !this.followed && s.terrainAt) {
      const t = this.controls.target;
      const ground = s.terrainAt(t.x, t.z);
      const was = this.eased ?? ground;
      this.eased = was + (ground - was) * (1 - Math.exp(-HABITAT_HOST.terrainRate * dt));
      t.y += this.eased - was;
      s.camera.position.y += this.eased - was;
    } else this.eased = null;
    this.hold();
  }

  /**
   * The camera and the point it looks at kept in the scene's box, and the camera where it may stand, in the box and its
   * room over what is solid under it, its distance kept (M115, the user's report: "it keeps zooming in and out even if i
   * use 1 finger"): a turn that would take it into the floor, an outcrop, the surface or the box's side stops there, the
   * camera at the angle nearest the turn's from which it may (`nearestFree`), or else where it last stood, then aimed
   * again; or else, with the point it looks at, where both last stood, slid along what stops it where it may, a pan that
   * would take it where it may not stand stopping there (M228's review: lifted straight, it looked down past the Reef's
   * 25° and drew back on its own, 2.40 m to 3.48 m, panned at its nearest); and, where an animal is followed or none is
   * to be had, kept to that point at a tilt it allows, drawn in, turned round it, or at last lifted with it. Clamped and
   * lifted straight, as it
   * was, a camera a finger turned came to stand nearer or farther each frame, MEASURED in Chromium as a phone and by the
   * review: on the Upper slope 16.7 m off, 27.6 m after three drags upward; in the Blue water 26.0 m, 16.6 m after a
   * drag down, the surface stopping it.
   */
  private hold(): void {
    const s = this.active;
    if (!s) return;
    const { bounds, clearance } = s.profile;
    const [min, max] = [new THREE.Vector3(...bounds.min), new THREE.Vector3(...bounds.max)];
    // The point it looks at in the box and on the floor, the camera moved with it as far: a pan or a follow it stops
    // stops the camera too, its distance kept (the review's: a two-finger pan down into the floor had drawn the camera
    // in, 5.0 m to 1.1 m on the reef, while the target stood on the floor).
    const target = this.controls.target;
    const was = this.probe.copy(target);
    target.clamp(min, max);
    const under = s.groundAt(target.x, target.z);
    if (target.y < under) target.y = under;
    const c = s.camera.position.add(was.subVectors(target, was));
    const e = 1e-9;
    const free = (p: THREE.Vector3): boolean => p.x >= min.x - e && p.x <= max.x + e && p.z >= min.z - e && p.z <= max.z + e
      && p.y >= min.y - e && p.y <= max.y + e && p.y >= Math.min(s.groundAt(p.x, p.z) + clearance, max.y) - e;
    if (!free(c)) {
      const probe = this.probe;
      const turn = new THREE.Spherical().setFromVector3(probe.copy(c).sub(target));
      const [lo, hi] = [this.controls.minPolarAngle, this.controls.maxPolarAngle];
      // Where the camera may stand round the point at a distance and a turn, at the tilt nearest its own, or null.
      const round = (radius: number, theta: number, least = lo): THREE.Vector3 | null => {
        if (radius <= 1e-6) return null;
        const phi = nearestFree(turn.phi, least, hi, (a) => free(probe.setFromSphericalCoords(radius, a, theta).add(target)));
        return phi === null ? null : probe.setFromSphericalCoords(radius, phi, theta).add(target);
      };
      // Past a ridge (M241): where no tilt it allows is free, lifted as little as it must, to the scene's ridge tilt at
      // most, and the controls' least kept there until it eases back (else their next update would drop it at once).
      // Tried before where it last stood and before an animal followed is drawn in, so a turn or a zoom out that meets
      // an outcrop is tilted over it, not refused, and a follow behind a ridge tilts before it closes in.
      const lifted = (): THREE.Vector3 | null => {
        if (this.ridgeLeast === null || this.ridgeLeast >= lo) return null;
        const at = round(turn.radius, turn.theta, this.ridgeLeast);
        if (at) this.controls.minPolarAngle = Math.min(lo, new THREE.Spherical().setFromVector3(this.step.copy(at).sub(target)).phi);
        return at;
      };
      const tilted = round(turn.radius, turn.theta) ?? lifted() ?? (this.held && free(probe.copy(target).add(this.held)) ? probe : null);
      if (tilted) c.copy(tilted);
      else if (!this.followed && this.stood && free(this.stood.camera)) {
        // Slid along what stops it where it may, a way at a time, the point it looks at kept in the box and over the
        // floor (M228's second review: put back whole, a pan sideways from where a pan forward had stopped did nothing,
        // the float's noise along the stop taking it in; its fourth: the point not kept, a turn at the box's side sank it
        // 1.2 m under the floor).
        const { camera: from, target: aim } = this.stood;
        const step = this.step.subVectors(c, from);
        const slid = [[0, 1, 1], [1, 1, 0]].map(([kx, ky, kz]) => new THREE.Vector3(step.x * (kx as number), step.y * (ky as number), step.z * (kz as number)))
          .find((d) => {
            const [p, q] = [from.clone().add(d), aim.clone().add(d)];
            return free(p) && q.x >= min.x - e && q.x <= max.x + e && q.z >= min.z - e && q.z <= max.z + e && q.y <= max.y + e && q.y >= s.groundAt(q.x, q.z) - e;
          });
        target.copy(aim).add(slid ?? probe.set(0, 0, 0));
        c.copy(from).add(slid ?? probe.set(0, 0, 0));
      } else {
        // An animal followed is kept to and kept in the middle, the tilt kept (M228's reviews: put back where both last
        // stood, a follow into the reef stopped there for good; moved in from the box's side with the point it looks
        // at, the animal 38° to 65° off the middle, past a phone's 16°): the camera drawn in, no nearer than the zoom's
        // nearest; else turned round the animal within the camera's turn; and only where neither is to be had, lifted
        // with the point it looks at over the floor, at the steepest tilt allowed and the nearest, moved in from the
        // box's side with it.
        const near = Math.min(this.controls.minDistance, turn.radius);
        let [out, got]: [number, THREE.Vector3 | null] = [turn.radius, null];
        for (let k = 1; k <= 16 && !got && turn.radius - near > 1e-6; k++) {
          const radius = turn.radius - ((turn.radius - near) * k) / 16;
          got = round(radius, turn.theta)?.clone() ?? null;
          if (!got) out = radius;
          else {
            // The farthest it may stand, between the last distance closed and this one.
            let [closed, open] = [out, radius];
            for (let i = 0; i < 12; i++) {
              const mid = (closed + open) / 2;
              const at = round(mid, turn.theta);
              if (at) [open, got] = [mid, at.clone()];
              else closed = mid;
            }
          }
        }
        const [west, east] = [this.controls.minAzimuthAngle, this.controls.maxAzimuthAngle];
        for (let k = 1; k <= 30 && !got; k++) {
          for (const theta of [turn.theta - (k * Math.PI) / 90, turn.theta + (k * Math.PI) / 90]) {
            if (!got && within(theta, west, east)) got = round(near, theta)?.clone() ?? null;
          }
        }
        if (got) c.copy(got);
        else {
          c.copy(probe.setFromSphericalCoords(near, lo, turn.theta).add(target));
          const inside = this.step.copy(c).clamp(min, max).sub(c);
          c.add(inside);
          target.add(inside);
          const rise = Math.max(0, Math.min(s.groundAt(c.x, c.z) + clearance, max.y) - c.y);
          c.y += rise;
          target.y = Math.max(target.y + rise, s.groundAt(target.x, target.z));
        }
      }
      s.camera.lookAt(target);
    }
    this.held = (this.held ?? new THREE.Vector3()).copy(c).sub(target);
    if (this.stood) {
      this.stood.camera.copy(c);
      this.stood.target.copy(target);
    } else this.stood = { camera: c.clone(), target: target.clone() };
  }

  /** Whether a move to a view is under way. */
  isMoving(): boolean {
    return this.moving !== null;
  }
}

/** OrbitControls' own moves the host's two fingers make (`gesture`), as its touch handlers make them. */
interface OrbitInternals {
  _panUp(distance: number, matrix: THREE.Matrix4): void;
  _sphericalDelta: THREE.Spherical;
  _panOffset: THREE.Vector3;
  _scale: number;
  _rotateLeft(angle: number): void;
  _rotateUp(angle: number): void;
  _dollyOut(scale: number): void;
  _updateZoomParameters(x: number, y: number): void;
}

/**
 * A habitat camera's lens on a canvas `w` by `h` under the panels' insets: the scene's field of view for the part they
 * leave, widened to the larger frame centered on it, as the whole tank's (`scene.ts`).
 */
export function fitLens(camera: THREE.PerspectiveCamera, profile: Pick<HabitatCameraProfile, 'fov' | 'near' | 'far'>, w: number, h: number, insets: Insets): void {
  const { width: vw, height: vh, fullWidth: fullW, fullHeight: fullH, offsetX, offsetY } = visibleRect(w, h, insets);
  const half = (profile.fov(vw / vh) * Math.PI) / 360;
  camera.fov = (Math.atan(Math.tan(half) * (fullH / vh)) * 360) / Math.PI;
  camera.aspect = fullW / fullH;
  camera.near = profile.near;
  camera.far = profile.far;
  if (fullW !== w || fullH !== h) camera.setViewOffset(fullW, fullH, offsetX, offsetY, w, h);
  else camera.clearViewOffset();
  camera.updateProjectionMatrix();
}

/**
 * Whether a turn round, radians, is within the controls' turn from `lo` to `hi`, read as OrbitControls reads them: each
 * wrapped into a half turn either way, the turn across the seam where `lo` comes past `hi` (M228's fifth review: read
 * as they stand, the Blue water's and the Twilight's turn, 90° to 270°, refused all of it past 180°).
 */
export function within(theta: number, lo: number, hi: number): boolean {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return true;
  const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
  const [a, b, t] = [wrap(lo), wrap(hi), wrap(theta)];
  return a <= b ? t >= a - 1e-12 && t <= b + 1e-12 : t >= a - 1e-12 || t <= b + 1e-12;
}

/**
 * The polar angle nearest `phi`, within `lo` to `hi`, at which `free` holds (the camera may stand there): tried a degree
 * at a time either way, upward first where both are as near, then found between the last angle that did not hold and
 * the first that does to a ten-millionth of a degree; null where none does.
 */
function nearestFree(phi: number, lo: number, hi: number, free: (phi: number) => boolean): number | null {
  const [top, bottom] = [Math.max(0, lo), Math.min(Math.PI, hi)];
  const start = Math.min(bottom, Math.max(top, phi));
  if (start !== phi && free(start)) return start;
  const step = Math.PI / 180;
  const last = [start, start];
  const done = [false, false];
  for (let k = 1; !(done[0] && done[1]); k++) {
    for (const way of [0, 1] as const) {
      if (done[way]) continue;
      const next = Math.min(bottom, Math.max(top, start + (way === 0 ? -k : k) * step));
      if (next === last[way]) {
        done[way] = true;
        continue;
      }
      if (free(next)) {
        let [bad, good] = [last[way] as number, next];
        for (let i = 0; i < 24; i++) {
          const mid = (bad + good) / 2;
          if (free(mid)) good = mid;
          else bad = mid;
        }
        return good;
      }
      last[way] = next;
    }
  }
  return null;
}
